// Small canvas-drawn readout of the active Classical match: round + dealer,
// plus each seat's running score. Same category as `SceneRender.ts`'s
// `drawWallCount` — SCREEN-space table furniture, drawn every frame, never
// panned/zoomed. Positioned top-right of `layout.table`, just under the pause
// gear (wall count owns top-left) so none of the three collide.

import { PAUSE_GEAR_MARGIN, PAUSE_GEAR_SIZE } from '@bn-games/render-helpers';
import { t } from '../i18n/I18n.js';
import { windLabel } from './windLabel.js';
import type { MatchState, Round } from '../match/MatchState.js';
import { SEATS, type Seat } from '../game/types.js';
import type { LayoutConfig } from '../layout/types.js';
import type { RenderPalette } from '../render/palette.js';
import {
  MATCH_HUD_LINE_HEIGHT_PX,
  NUMERAL_FONT_STACK,
  WALL_COUNT_FONT_PX,
  WALL_COUNT_PADDING_PX,
} from '../utils/constants.js';

function roundWindLabel(round: Round): string {
  return round === 'south' ? t('game.seatSouth') : t('game.seatEast');
}

/**
 * The viewer's own seat reads "You"; every other seat reads its wind — which
 * is dealer-relative, never the seat index (see `game/seatWinds.ts`). Online
 * this also rules out the other tempting-but-wrong source: the room's `seats`
 * array, which rematch rotates independently of the dealer.
 */
function seatLabel(seat: Seat, localSeat: Seat, dealer: Seat): string {
  return seat === localSeat ? t('match.you') : windLabel(seat, dealer);
}

/** Small live readout of round/dealer + running per-seat scores, top-right of
 *  the felt. Only ever called for a Classical match in progress. `localSeat`
 *  is the viewer — seat 0 solo, whatever the server sat us in online. */
export function drawMatchHud(
  ctx: CanvasRenderingContext2D,
  layout: LayoutConfig,
  palette: RenderPalette,
  matchState: MatchState,
  localSeat: Seat,
): void {
  const { dealer } = matchState;
  const lines: readonly string[] = [
    t('match.round', { round: roundWindLabel(matchState.round) }),
    // The dealer IS East by definition once winds are dealer-relative, so the
    // useful half of this line is whether the dealer is you.
    t('match.dealer', { seat: seatLabel(dealer, localSeat, dealer) }),
    ...SEATS.map((seat) =>
      t('match.score', {
        seat: seatLabel(seat, localSeat, dealer),
        points: matchState.scores[seat],
      }),
    ),
  ];

  ctx.save();
  ctx.font = `${WALL_COUNT_FONT_PX}px ${NUMERAL_FONT_STACK}`;
  ctx.fillStyle = palette.compassText;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'top';
  const x = layout.table.x + layout.table.width - WALL_COUNT_PADDING_PX;
  // The pause gear owns the corner itself; the readout starts under it.
  let y = layout.table.y + WALL_COUNT_PADDING_PX + PAUSE_GEAR_SIZE + PAUSE_GEAR_MARGIN;
  for (const line of lines) {
    ctx.fillText(line, x, y);
    y += MATCH_HUD_LINE_HEIGHT_PX;
  }
  ctx.restore();
}
